import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import constants from 'constants/constants';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useProjectScorecardsLogic } from 'components/widgets/ProjectScorecards/ProjectScorecards.logic';
import scorecardsClassName from 'components/widgets/ProjectScorecards/ProjectScorecards.module.scss';
import { useProjectScorecardsProvider } from 'components/widgets/ProjectScorecards/ProjectScorecards.provider';
import { columnsV8, initialState } from 'components/widgets/ProjectScorecards/projectScorecards.utils';

const { id, componentKey, endpoint } = widgetsConfigurations.projectScorecards;

export const ProjectScorecards = ({
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    globalFilterData,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.projectScorecards.title,
    setIsPayloadReady,
}: IComponentProps) => {
    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    // Provider
    const { ui, api } = useProjectScorecardsProvider({
        componentKey,
        widgetId,
        globalFilterData,
        localFilters,
        table,
    });

    // Widget
    const { route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getProjectScoreCardsSummary.payload,
        widgetEndpoint: endpoint,
    });

    // Logic
    const { logic } = useProjectScorecardsLogic({ api, setIsPayloadReady, widgetViewMode });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        widgetId={widgetId}
                        route={route}
                        projectId={null}
                        contractId={null}
                        callBack={table.setWidgetColumns}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={constants.NO_PROJECT_CONTRACT_ID}
                        contractId={constants.NO_PROJECT_CONTRACT_ID}
                        uiState={ui.state}
                        callBack={table.setWidgetColumns}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.getProjectScoreCardsSummary.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={ui.subTitleForExport} />
                    ),
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <ColumnsManager
                            onOpenModal={() => table.setModalColumnsOpen(true)}
                            onHideModal={() => table.setModalColumnsOpen(false)}
                            showModal={table.modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={table.tableInstance}
                            initialState={initialState}
                            columns={columnsV8}
                            widgetViewMode={widgetViewMode}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getProjectScoreCardsSummary.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        key={'pinboardPills'}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
            >
                <TableContainer>
                    {table.updatedInitialState && (
                        <TableV8
                            data={logic.scoreCardsTableData}
                            columns={columnsV8}
                            isLoading={
                                api.getProjectScoreCardsSummary.instance.isLoading ||
                                api.getProjectScoreCardsSummary.instance.isFetching
                            }
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                            columnFilters={table.columnFilters}
                            setColumnFilters={table.setColumnFilters}
                            manualFiltering={false}
                            manualSorting={false}
                            tableClassName={
                                WidgetViewModeUtils.isReportView(widgetViewMode) ? scorecardsClassName.table : ''
                            }
                        />
                    )}
                </TableContainer>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
